import React from "react";
import { withTranslation } from "react-i18next";
import {
  getClientUserVessels,
  getCurrentUserName,
  isApiSuccess,
  normalizeVesselsResponse,
  syncClientUserVessels,
} from "../Actions/ClientContactUserAPI";
import "./UserManagementMaster.css";

class ClientContactUserVesselModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedSearch: "",
      remainingSearch: "",
      selectedVessels: [],
      remainingVessels: [],
      draftSelectedIds: [],
      loading: false,
      saving: false,
    };
    this.searchTimer = null;
    this.vesselRequestId = 0;
  }

  componentDidMount() {
    this.syncFromProps();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.show !== this.props.show ||
      prevProps.usersApplicableClientId !== this.props.usersApplicableClientId
    ) {
      this.syncFromProps();
    }
  }

  componentWillUnmount() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  }

  syncFromProps() {
    const { show, usersApplicableClientId } = this.props;
    if (!show || !usersApplicableClientId) {
      this.vesselRequestId += 1;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.setState({
        selectedSearch: "",
        remainingSearch: "",
        selectedVessels: [],
        remainingVessels: [],
        draftSelectedIds: [],
        loading: false,
        saving: false,
      });
      return;
    }

    this.setState(
      {
        selectedSearch: "",
        remainingSearch: "",
        selectedVessels: [],
        remainingVessels: [],
        draftSelectedIds: [],
        saving: false,
      },
      () => this.loadVessels("", "", true)
    );
  }

  loadVessels(selectedSearch, remainingSearch, resetDraft = false) {
    const { usersApplicableClientId } = this.props;
    if (!usersApplicableClientId) {
      return;
    }

    this.setState({ loading: true });

    const requestId = ++this.vesselRequestId;

    getClientUserVessels(usersApplicableClientId, selectedSearch, remainingSearch)
      .then((response) => {
        if (requestId !== this.vesselRequestId) {
          return;
        }

        const { selected, remaining } = normalizeVesselsResponse(response.data);

        this.setState((prevState) => ({
          selectedVessels: selected,
          remainingVessels: remaining,
          draftSelectedIds: resetDraft
            ? selected.map((vessel) => vessel.shipId)
            : prevState.draftSelectedIds,
          loading: false,
        }));
      })
      .catch(() => {
        if (requestId !== this.vesselRequestId) {
          return;
        }

        this.setState({ loading: false });
      });
  }

  scheduleSearch(type, value) {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = setTimeout(() => {
      const { selectedSearch, remainingSearch } = this.state;
      if (type === "selected") {
        this.loadVessels(value, remainingSearch);
      } else {
        this.loadVessels(selectedSearch, value);
      }
    }, 500);
  }

  getAllVessels() {
    const { selectedVessels, remainingVessels } = this.state;
    const vesselMap = {};

    [...selectedVessels, ...remainingVessels].forEach((vessel) => {
      vesselMap[vessel.shipId] = vessel;
    });

    return Object.values(vesselMap);
  }

  getSelectedVessels() {
    const { draftSelectedIds } = this.state;
    const allVessels = this.getAllVessels();

    return draftSelectedIds
      .map((shipId) => allVessels.find((vessel) => vessel.shipId === shipId))
      .filter(Boolean);
  }

  getRemainingVessels() {
    const { draftSelectedIds } = this.state;

    return this.getAllVessels().filter(
      (vessel) => !draftSelectedIds.includes(vessel.shipId)
    );
  }

  addToSelected(shipId) {
    this.setState((prevState) => {
      if (prevState.draftSelectedIds.includes(shipId)) {
        return null;
      }
      return {
        draftSelectedIds: [...prevState.draftSelectedIds, shipId],
      };
    });
  }

  removeFromSelected(shipId) {
    this.setState((prevState) => ({
      draftSelectedIds: prevState.draftSelectedIds.filter((id) => id !== shipId),
    }));
  }

  clearAllSelected() {
    this.setState({ draftSelectedIds: [], selectedSearch: "" }, () =>
      this.loadVessels("", this.state.remainingSearch, true)
    );
  }

  clearRemainingSearch() {
    this.setState({ remainingSearch: "" }, () =>
      this.loadVessels(this.state.selectedSearch, "", false)
    );
  }

  handleSave = () => {
    const { usersApplicableClientId, onSave, t } = this.props;
    const { draftSelectedIds } = this.state;

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }

    this.setState({ saving: true, loading: false });

    syncClientUserVessels({
      usersApplicableClientId,
      shipIds: draftSelectedIds.filter((shipId) => shipId != null),
      modifiedBy: getCurrentUserName(),
    })
      .then((response) => {
        if (isApiSuccess(response.data)) {
          this.setState({ saving: false, loading: false });
          onSave(draftSelectedIds.filter((shipId) => shipId != null).length);
          return;
        }

        this.setState({ saving: false });
        window.alert(response.data || t("CommonText.500ErrorMessage"));
      })
      .catch(() => {
        this.setState({ saving: false, loading: false });
        window.alert(t("CommonText.500ErrorMessage"));
      });
  };

  renderSearchField(value, onChange, t) {
    return (
      <div className="um-transfer-panel-toolbar">
        <div className="um-transfer-filters um-transfer-filters-search-only">
          <div className="um-input-shell um-modal-search">
            <span className="um-input-icon">
              <i className="fa fa-search" aria-hidden="true"></i>
            </span>
            <input
              className="form-control"
              type="text"
              placeholder={t("content.Search")}
              value={value}
              onChange={onChange}
            />
          </div>
        </div>
      </div>
    );
  }

  renderVesselTable({ tableClassName, headerCells, bodyRows, emptyMessage }) {
    const hasRows = bodyRows.length > 0;

    return (
      <div className={`um-transfer-table-wrap ${hasRows ? "" : "is-empty"}`}>
        <table className={`um-transfer-table ${tableClassName}`}>
          <colgroup>
            <col className="um-col-name" />
            <col className="um-col-type" />
            <col className="um-col-action" />
          </colgroup>
          <thead>
            <tr>{headerCells}</tr>
          </thead>
          {hasRows ? <tbody>{bodyRows}</tbody> : null}
        </table>
        {!hasRows ? (
          <div className="um-transfer-empty-state">{emptyMessage}</div>
        ) : null}
      </div>
    );
  }

  renderSelectedPanel(t, selectedVessels) {
    const { selectedSearch } = this.state;

    return (
      <div className="um-transfer-panel um-transfer-panel-selected">
        <div className="um-transfer-panel-header um-transfer-panel-header-between">
          <span>
            {t("ClientContactUserManagementContent.SelectedVessels")} (
            {this.state.draftSelectedIds.length})
          </span>
          <button
            type="button"
            className="um-link-btn"
            onClick={() => this.clearAllSelected()}
          >
            {t("ClientContactUserManagementContent.ClearAll")}
          </button>
        </div>
        {this.renderSearchField(
          selectedSearch,
          (e) => {
            const value = e.target.value;
            this.setState({ selectedSearch: value });
            this.scheduleSearch("selected", value);
          },
          t
        )}
        <div className="um-transfer-panel-body">
          {this.renderVesselTable({
            tableClassName: "um-transfer-table-selected",
            emptyMessage: t(
              "ClientContactUserManagementContent.NoVesselsSelected"
            ),
            headerCells: (
              <>
                <th className="um-transfer-col-name" scope="col">
                  {t("ClientContactUserManagementContent.VesselName")}
                </th>
                <th className="um-transfer-col-type" scope="col">
                  {t("ClientContactUserManagementContent.VesselType")}
                </th>
                <th
                  className="um-transfer-col-action"
                  scope="col"
                  aria-label={t("ClientContactUserManagementContent.Actions")}
                />
              </>
            ),
            bodyRows: selectedVessels.map((vessel) => (
              <tr key={vessel.shipId}>
                <td className="um-transfer-col-name" title={vessel.shipName}>
                  <span className="um-transfer-cell-text">{vessel.shipName}</span>
                </td>
                <td className="um-transfer-col-type" title={vessel.vesselType}>
                  <span className="um-transfer-cell-text">{vessel.vesselType}</span>
                </td>
                <td className="um-transfer-action-cell">
                  <button
                    type="button"
                    className="um-transfer-remove-btn"
                    onClick={() => this.removeFromSelected(vessel.shipId)}
                    aria-label={t("content.Delete")}
                  >
                    <i className="fa fa-times" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            )),
          })}
        </div>
      </div>
    );
  }

  renderRemainingPanel(t, remainingVessels) {
    const { remainingSearch } = this.state;

    return (
      <div className="um-transfer-panel um-transfer-panel-available">
        <div className="um-transfer-panel-header um-transfer-panel-header-between">
          <span>
            {t("ClientContactUserManagementContent.AvailableVessels")} (
            {remainingVessels.length})
          </span>
          <button
            type="button"
            className="um-link-btn"
            onClick={() => this.clearRemainingSearch()}
          >
            {t("ClientContactUserManagementContent.ClearAll")}
          </button>
        </div>
        {this.renderSearchField(
          remainingSearch,
          (e) => {
            const value = e.target.value;
            this.setState({ remainingSearch: value });
            this.scheduleSearch("remaining", value);
          },
          t
        )}
        <div className="um-transfer-panel-body">
          {this.renderVesselTable({
            tableClassName: "um-transfer-table-available",
            emptyMessage: t(
              "ClientContactUserManagementContent.NoVesselsAvailable"
            ),
            headerCells: (
              <>
                <th className="um-transfer-col-name" scope="col">
                  {t("ClientContactUserManagementContent.VesselName")}
                </th>
                <th className="um-transfer-col-type" scope="col">
                  {t("ClientContactUserManagementContent.VesselType")}
                </th>
                <th
                  className="um-transfer-col-action"
                  scope="col"
                  aria-label={t("ClientContactUserManagementContent.Actions")}
                />
              </>
            ),
            bodyRows: remainingVessels.map((vessel) => (
              <tr key={vessel.shipId}>
                <td className="um-transfer-col-name" title={vessel.shipName}>
                  <span className="um-transfer-cell-text">{vessel.shipName}</span>
                </td>
                <td className="um-transfer-col-type" title={vessel.vesselType}>
                  <span className="um-transfer-cell-text">{vessel.vesselType}</span>
                </td>
                <td className="um-transfer-action-cell">
                  <button
                    type="button"
                    className="um-transfer-add-btn"
                    onClick={() => this.addToSelected(vessel.shipId)}
                    aria-label={t("ClientContactUserManagementContent.AddVessels")}
                  >
                    <i className="fa fa-plus" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            )),
          })}
        </div>
      </div>
    );
  }

  render() {
    const { show, clientName, onClose, t } = this.props;
    if (!show) {
      return null;
    }

    const selectedVessels = this.getSelectedVessels();
    const remainingVessels = this.getRemainingVessels();
    const { loading, saving } = this.state;

    return (
      <div className="um-modal-overlay" onClick={onClose}>
        <div
          className="um-modal um-vessel-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="um-modal-header">
            <h5>{t("ClientContactUserManagementContent.AddVessels")}</h5>
            <button type="button" className="um-modal-close" onClick={onClose}>
              <span className="um-btn-clear-mark" aria-hidden="true">
                ×
              </span>
            </button>
          </div>

          <div className="um-modal-info">
            <i className="fa fa-info-circle" aria-hidden="true"></i>
            <span>
              {t("ClientContactUserManagementContent.SelectVesselsFor")}{" "}
              <strong>{clientName}</strong>
            </span>
          </div>

          {loading ? (
            <div className="um-empty-state">Loading...</div>
          ) : (
            <div className="um-transfer-layout um-transfer-layout-simple">
              {this.renderSelectedPanel(t, selectedVessels)}
              {this.renderRemainingPanel(t, remainingVessels)}
            </div>
          )}

          <div className="um-modal-footer">
            <button
              type="button"
              className="btn btn-light text-uppercase um-btn um-btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              {t("ComplianceandBudgetScreen.Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary text-uppercase um-btn um-btn-primary"
              onClick={this.handleSave}
              disabled={saving || loading}
            >
              {t("ClientContactUserManagementContent.AddVessels")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation()(ClientContactUserVesselModal);
